import { format } from 'date-fns'

import { DATE_SHORT_FORMAT } from '../../../constants/date'
import { toUkTimezoneDate } from '../../../utils/date'
import {
  TableAsyncBuildingGroupListCell,
  TableBuildingCell,
  TableDateCell,
  TableEmptyCell,
  TableFlatListCell,
  TablePhoneCell,
  TablePhoneListCell,
  TableRoleCell,
  TableStatusCell,
  TableTextCell,
  TableUserCell,
  TextListCell,
  toPhoneCellMultiValue,
  toTableCellMultiValue,
} from '../../Table'
import { toUserMultiValue } from '../../User'

import { InhabitantTableCellName } from './InhabitantTableRow.constants'

function toFinancialCellValue(amount, currency) {
  return `${amount} ${currency}`
}

function toContractsCellValue(value, ukTimeZone) {
  const flatNumber = value?.flat?.number ?? value?.flat_number

  if (!flatNumber) {
    return null
  }

  const { locale, timezone } = ukTimeZone

  const startDate = value.start_date
    ? format(toUkTimezoneDate(value.start_date, timezone), DATE_SHORT_FORMAT, {
        locale,
      })
    : null

  return {
    children: startDate ? `${flatNumber} — ${startDate}` : `${flatNumber}`,
  }
}

export function renderCell({
  context,
  item,
  name,
  statuses,
  ukTimeZone,
  onChangeAddress,
  ...restProps
}) {
  const { [name]: value } = item

  switch (name) {
    case InhabitantTableCellName.FullName:
      return (
        <TableUserCell
          {...restProps}
          value={value ? toUserMultiValue(item) : null}
        />
      )

    case InhabitantTableCellName.Building:
      return (
        <TableBuildingCell
          {...restProps}
          canChange={item.permissions.can_manage_flats}
          context={context}
          id={item.id}
          value={value}
          onChangeAddress={onChangeAddress}
        />
      )

    case InhabitantTableCellName.BuildingGroup:
      return (
        <TableAsyncBuildingGroupListCell
          {...restProps}
          context={context}
          id={item.id}
          value={value}
        />
      )

    case InhabitantTableCellName.Contracts:
      return (
        <TableFlatListCell
          {...restProps}
          value={value
            ?.map(contract => toContractsCellValue(contract, ukTimeZone))
            .filter(Boolean)}
        />
      )

    case InhabitantTableCellName.Role:
      return <TableRoleCell {...restProps} value={value} />

    case InhabitantTableCellName.Status:
      return (
        <TableStatusCell {...restProps} statuses={statuses} value={value} />
      )

    case InhabitantTableCellName.PartnerPhone:
    case InhabitantTableCellName.Phone:
      return <TablePhoneCell {...restProps} value={value} />

    case InhabitantTableCellName.AdditionalEmails:
      return (
        <TextListCell
          {...restProps}
          value={value?.map(toTableCellMultiValue)}
        />
      )

    case InhabitantTableCellName.AdditionalPhones:
      return (
        <TablePhoneListCell
          {...restProps}
          value={value?.map(toPhoneCellMultiValue)}
        />
      )

    case InhabitantTableCellName.FinancialDeposit:
    case InhabitantTableCellName.FinancialOpenAmount:
      return (
        <TableTextCell
          {...restProps}
          value={toFinancialCellValue(value?.amount, value?.currency)}
        />
      )

    case InhabitantTableCellName.PartnerSalutation:
    case InhabitantTableCellName.PersonalPreferredCommunication:
    case InhabitantTableCellName.PersonalSalutation:
    case InhabitantTableCellName.PersonalStatus:
      return <TableTextCell {...restProps} value={value?.value} />

    case InhabitantTableCellName.ArchivedAt:
    case InhabitantTableCellName.Created:
    case InhabitantTableCellName.LastInvited:
    case InhabitantTableCellName.Registered:
    case InhabitantTableCellName.Updated:
      return <TableDateCell {...restProps} value={value} />

    case InhabitantTableCellName.AddressAddress:
    case InhabitantTableCellName.AddressAddress2:
    case InhabitantTableCellName.AddressCity:
    case InhabitantTableCellName.AddressCountry:
    case InhabitantTableCellName.AddressPostcode:
    case InhabitantTableCellName.AddressStreet:
    case InhabitantTableCellName.Email:
    case InhabitantTableCellName.ExternalId:
    case InhabitantTableCellName.FinancialBankName:
    case InhabitantTableCellName.FinancialBic:
    case InhabitantTableCellName.FinancialIban:
    case InhabitantTableCellName.FinancialPaymentType:
    case InhabitantTableCellName.FirstName:
    case InhabitantTableCellName.LastName:
    case InhabitantTableCellName.ORAGDepositType:
    case InhabitantTableCellName.ORAGFundings:
    case InhabitantTableCellName.ORAGGuaranteedValue:
    case InhabitantTableCellName.ORAGRemedy:
    case InhabitantTableCellName.ORAGStopOverdueNotice:
    case InhabitantTableCellName.ORAGTerminationDenied:
    case InhabitantTableCellName.ORAGTerminationTime:
    case InhabitantTableCellName.ORAGTerminationTimeframe:
    case InhabitantTableCellName.PartnerEmail:
    case InhabitantTableCellName.PartnerFirstName:
    case InhabitantTableCellName.PartnerFullName:
    case InhabitantTableCellName.PartnerLastName:
    case InhabitantTableCellName.PartnerMiddleName:
    case InhabitantTableCellName.PartnerTitle:
    case InhabitantTableCellName.PersonalFullName:
    case InhabitantTableCellName.PersonalGdprInfo:
    case InhabitantTableCellName.PersonalMiddleName:
    case InhabitantTableCellName.PersonalNameInfo:
    case InhabitantTableCellName.PersonalTitle:
      return <TableTextCell {...restProps} value={value} />

    default:
      return <TableEmptyCell {...restProps} />
  }
}
